import { Injectable } from '@angular/core';
import { dataMember, required, typedArray } from 'santee-dcts';
import { keyBy } from 'lodash';
import { CategoryNaturalKey } from '../documents';
import { User } from '../user/user';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export type EsaFeatures = 'investigatorBoxplot' | 'sponsorBoxPlots';

export class CorporateMandate {

    @dataMember()
    @required()
    public readonly key: string;

    @dataMember()
    @required()
    public readonly value: string;
}

export class FeatureToggler {

    @dataMember()
    @required()
    public readonly key: EsaFeatures;

    @dataMember()
    @required()
    public readonly value: boolean;
}

export class Link {

    @dataMember()
    @required()
    public readonly key: string;

    @dataMember()
    @required()
    public readonly text: string;

    @dataMember()
    @required()
    public readonly url: string;

    @dataMember()
    @required()
    public readonly shouldOpenInNewTab: boolean;

    public navigate(): void {
        if (!this.shouldOpenInNewTab) {
            window.location.href = this.url;
        } else {
            window.open(this.url);
        }
    }
}

export class UploadedFileExtension {
    @dataMember()
    @required()
    public readonly categoryId: string;

    @dataMember()
    @required()
    @typedArray(String)
    public readonly extensions: string[];   
}

@Injectable()
export class Settings {
    @dataMember()
    @required()
    public readonly angularEnableProdMode: boolean;

    @dataMember()
    @required()
    public readonly shouldUseLegacyOktaWidget: boolean;

    @dataMember()
    @required()
    public readonly oktaWidgetIdpDiscovery: boolean;

    @dataMember()
    @required()
    public readonly oktaWidgetTitle: string;

    @dataMember()
    @required()
    public readonly oktaWidgetLogoPath: string;

    @dataMember()
    @required()
    public readonly apiUrl: string;

    @dataMember()
    @required()
    public readonly clientId: string;

    @dataMember()
    @required()
    public readonly issuer: string;

    @dataMember()
    @required()
    public readonly inactivityPeriodSeconds: number;

    @dataMember()
    @required()
    public readonly timeoutPeriodSeconds: number;

    @dataMember()
    @required()
    public readonly maxNumberOfDocumentsForDownload: number;

    @dataMember()
    @required()
    public readonly maxNumberOfSponsorLabReports: number;

    @dataMember()
    @required()
    public readonly maxNumberOfVisibleFilterOptions: number;

    @dataMember()
    @required()
    public readonly minFilterSearchTermLengthForLargeArrays: number;

    @dataMember()
    @required()
    public readonly maxNumberOfTrends: number;

    @dataMember()
    @required()
    public readonly maxSizeOfUploadedDocument: number;

    @dataMember()
    @required()
    public readonly extremelyLargeSiteThreshhold: number;

    @dataMember()
    @required()
    public readonly activityReportsFilterDefaultDateRange: number;

    @dataMember()
    @required()
    public readonly sqlRequestLimitForActivityReports: number;

    @dataMember()
    @required()
    public readonly documentsFilterDefaultDateRange: number;

    @dataMember()
    @required()
    public readonly kitInventoryExpirationDefaultDateRange: number;

    @dataMember()
    @required()
    public readonly enableStudyHealthDashboard: boolean;

    @dataMember()
    @required()
    public readonly tableauAPI: string;
    
    @dataMember()
    @required()
    @typedArray(CorporateMandate)
    public readonly corporateMandates: CorporateMandate[];

    @dataMember()
    @required()
    @typedArray(FeatureToggler)
    public readonly featureTogglers: FeatureToggler[];

    @dataMember()
    @required()
    @typedArray(Link)
    public readonly links: Link[];

    @dataMember()
    @required()
    @typedArray(Link)
    public readonly idpLinks: Link[];

    //redirect user on login page automatically after JWT expiration
    public readonly redirectOnExpiration = true;

    @dataMember()
    public readonly emulatedUserId?: number;

    public get authenticationTokenPostFieldName() {
        return 'ESA-Authentication';
    }

    public get emulateAsParamName() {
        return `emulateAs`;
    }

    @dataMember()
    @required()
    @typedArray(UploadedFileExtension)
    public readonly uploadedFileExtensions: UploadedFileExtension[];

    @dataMember()
    @required()
    @typedArray(String)
    public readonly playableVideoFileExtensions: string[];

    @dataMember()
    @required()
    @typedArray(String)
    public readonly eQueryAttachmentsExtensions: string[];

    @dataMember()
    @required()
    public readonly eQueryMaxNumberOfAttachments: number;

    @dataMember()
    @required()
    public readonly eQueryMaxNumberOfAttachmentsReopenClosed: number;

    @dataMember()
    @required()
    public readonly eQueryMaxSizeOfAttachments: number;

    @dataMember()
    @required()
    public readonly eQueryMaxNumberOfAttachmentsPerIssue: number;

    @dataMember()
    @required()
    public readonly sampleTrackingCancelledSamplesHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingReceivedSamplesHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingInInventorySamplesHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingShippedFromLabcorpSamplesHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingPendingArrivalSamplesHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingShippedFromSiteToLabcorpSamplesHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingShippedFromSiteToThirdPartySamplesHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingDiscardedFromSpecimenManagementSamplesHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingRemovedFromSpecimenManagementSamplesHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingAllSamplesHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingAffectedByHoldSamplesHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingNotAvailableHelpText: string;

    @dataMember()
    @required()
    public readonly specimenManagementInventoryReportHelpText: string;

    @dataMember()
    @required()
    public readonly sampleTrackingReportHelpText: string;


    @dataMember()
    @required()
    public readonly pendoSponsorApiKey: string;

    @dataMember()
    @required()
    public readonly pendoInvestigatorApiKey: string;

    @dataMember()
    @required()
    public readonly eReqEnvironment: string;

    private corporateMandatesDictionaryStorage: { [key: string]: CorporateMandate };
    private featureTogglersDictionaryStorage: { [key: string]: FeatureToggler };

    private linksDictionaryStorage: { [key: string]: Link };

    public get linksDictionary() {
        if (!this.linksDictionaryStorage) {
            this.linksDictionaryStorage = {};
            if (this.links) {
                this.linksDictionaryStorage = keyBy(this.links, x => x.key);
            }
        }
        return this.linksDictionaryStorage;
    }

    private idpLinksDictionaryStorage: { [key: string]: Link };

    public get idplinksDictionary() {
        if (!this.idpLinksDictionaryStorage) {
            this.idpLinksDictionaryStorage = {};
            if (this.idpLinks) {
                this.idpLinksDictionaryStorage = keyBy(this.idpLinks, x => x.key);
            }
        }
        return this.idpLinksDictionaryStorage;
    }

    public get corporateMandatesDictionary() {
        if (!this.corporateMandatesDictionaryStorage) {
            this.corporateMandatesDictionaryStorage = {};
            if (this.corporateMandates) {
                this.corporateMandatesDictionaryStorage = keyBy(this.corporateMandates, x => x.key);
            }
        }
        return this.corporateMandatesDictionaryStorage;
    }

    public get featureTogglersDictionary() {
        if (!this.featureTogglersDictionaryStorage) {
            this.featureTogglersDictionaryStorage = {};
            if (this.featureTogglers) {
                this.featureTogglersDictionaryStorage = keyBy(this.featureTogglers, x => x.key);
            }
        }
        return this.featureTogglersDictionaryStorage;
    }

    public isFeatureEnabled(featureKey: EsaFeatures) {
        if (!this.featureTogglersDictionary[featureKey]) {
            return true;
        }
        return this.featureTogglersDictionary[featureKey].value;
    }

    public uploadedFileExtensionsByCategoryId(categoryId: CategoryNaturalKey): string[] | null {
        const item = this.uploadedFileExtensions.find(x => x.categoryId === categoryId);
        return item ? item.extensions : null;
    }

    public getSpecificUserApiUrl(user: User) {
        if (user.isInvestigator) {
            return `${this.apiUrl}/investigator`;
        } else if (user.isSponsor) {
            return `${this.apiUrl}/sponsor`;
        } else {
            throw User.notSupportedRoleError();
        }
    }

    public getSubDomain(): Observable<string> {
        let browserUrl = window.location.href;
        const appSubDomain$ = of(this.findSubDomain(browserUrl)).pipe(map(subdomain => subdomain));

        return appSubDomain$;
    }

    private findSubDomain(url: string): string {
        if (url.search(/invp/i) !== -1) {
            return 'invp';
        } else if (url.search(/xsp/i) !== -1) {
            return 'xsp';
        } else {
            return 'invp';
        }
    }
}

